import { useExactCvEdition } from '../../hooks/useExactCvEdition';
import { CvSelectedEditionPreview } from './CvSelectedEditionPreview';
import React, { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExportFormat } from '../../lib/cv-export';
import type { CvTargetKey } from '../../lib/cv-collaboration-api';
import { buildCvAccessInviteUrl, buildCvShareUrl } from '../../lib/cv-share-url';
import { withCvShareRef } from '@tabbio/utils/analytics/cv-traffic';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { NativeButton } from '../common/NativeButton';
import { CvEditionSelector } from './CvEditionSelector';
import { CvShareReleaseChoice } from './CvShareReleaseChoice';
import { cvReviewEnabled } from '../../lib/cv-release-api';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import {
  useCreateCvAccessGrant,
  useCreateCvShareLink,
  useCvAccessGrants,
  useCvActivity,
  useCvShareLinks,
  useRevokeCvAccessGrant,
  useUpdateCvShareLink,
} from '../../hooks/useCvCollaborationApi';

type Tab = 'link' | 'people' | 'download' | 'activity';

const FORMATS: ExportFormat[] = ['pdf', 'docx', 'txt', 'md'];

const TABS: Array<{ id: Tab; icon: string; labelKey: string }> = [
  { id: 'link', icon: 'link', labelKey: 'cv.sharePanel.tabs.link' },
  { id: 'download', icon: 'download', labelKey: 'cv.sharePanel.tabs.download' },
  { id: 'activity', icon: 'history', labelKey: 'cv.sharePanel.tabs.activity' },
];

export interface CVSharePanelProps {
  target: CvTargetKey;
  title: string;
  publicUrl?: string | null;
  initialVisibility?: CvVisibilityOptions;
  canDownloadFormat: (format: ExportFormat) => boolean;
  isFormatLoading: (format: ExportFormat) => boolean;
  onLockedFormat: () => void;
  onDownload: (
    format: ExportFormat,
    options: { variantId?: string | null; revisionId?: string | null; visibility: CvVisibilityOptions },
  ) => Promise<void>;
  /**
   * Whether the viewer may download this target at all
   * (`useCanDownloadCvTarget`). False hides the Download tab: a grantee
   * without the export permission is refused by the API, which is not a
   * paid-format lock. Defaults to true (the owner's own CV).
   */
  canDownload?: boolean;
}

export interface CvVisibilityOptions {
  showEmail: boolean;
  showPhone: boolean;
  showLocation: boolean;
  showLinks: boolean;
  showPhoto: boolean;
}

const DEFAULT_VISIBILITY: CvVisibilityOptions = {
  showEmail: true,
  showPhone: true,
  showLocation: true,
  showLinks: true,
  showPhoto: true,
};

function visibilityPayload(visibility: CvVisibilityOptions): Record<string, unknown> {
  return {
    showEmail: visibility.showEmail,
    showPhone: visibility.showPhone,
    showLocation: visibility.showLocation,
    showLinks: visibility.showLinks,
    showPhoto: visibility.showPhoto,
  };
}

export function CVSharePanel({
  target,
  title,
  publicUrl,
  initialVisibility,
  canDownloadFormat,
  isFormatLoading,
  onLockedFormat,
  onDownload,
  canDownload = true,
}: CVSharePanelProps) {
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const [tab, setTab] = useState<Tab>('link');
  const tabs = canDownload ? TABS : TABS.filter((item) => item.id !== 'download');
  const params = useLocalSearchParams<{ cvEdition?: string | string[] }>();
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(() =>
    cvReviewEnabled && typeof params.cvEdition === 'string' && params.cvEdition.length <= 192 ? params.cvEdition : null);
  const selectedEdition = useExactCvEdition(target, selectedVariantId, tab === 'download');
  const [releaseChoice, setReleaseChoice] = useState<{ mode: 'follow_approved' | 'exact'; releaseId?: string }>({ mode: 'follow_approved' });
  const [visibility, setVisibility] = useState<CvVisibilityOptions>(
    initialVisibility ?? DEFAULT_VISIBILITY,
  );
  const [allowDownload, setAllowDownload] = useState(false);
  const [email, setEmail] = useState('');
  const [downloadFormat, setDownloadFormat] = useState<ExportFormat | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const [lastShareUrl, setLastShareUrl] = useState<string | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  const [lastAccessMessage, setLastAccessMessage] = useState<string | null>(null);
  const [revokingGrantId, setRevokingGrantId] = useState<string | null>(null);

  const accessGrants = useCvAccessGrants(target);
  const createAccess = useCreateCvAccessGrant(target);
  const revokeAccess = useRevokeCvAccessGrant(target);
  const shareLinks = useCvShareLinks(target);
  const createShare = useCreateCvShareLink(target);
  const updateShare = useUpdateCvShareLink(target);
  const activity = useCvActivity(target);

  const activeShare = shareLinks.data?.[0] ?? null;

  useEffect(() => {
    setVisibility(initialVisibility ?? DEFAULT_VISIBILITY);
  }, [initialVisibility]);

  const handleCreateShare = async () => {
    const result = await createShare.mutateAsync({
      variantId: selectedVariantId,
      ...(cvReviewEnabled ? { releaseMode: releaseChoice.mode, releaseId: releaseChoice.releaseId } : {}),
      label: selectedVariantId
        ? t('cv.sharePanel.link.languageLabel')
        : t('cv.sharePanel.link.defaultLabel'),
      permissions: ['view'],
      visibility: visibilityPayload(visibility),
      allowDownload,
      revokeExisting: true,
    });
    setLastShareUrl(buildCvShareUrl(result.token, result.shareUrl));
  };

  // `channel` tags the owner's own share link with `?ref=` for their
  // analytics; an invite is addressed to one person and is copied as is.
  const handleCopy = async (value: string, channel?: 'copy') => {
    await navigator.clipboard.writeText(channel ? withCvShareRef(value, channel) : value);
    setCopiedValue(value);
    setTimeout(() => setCopiedValue(null), 1500);
  };

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    const result = await createAccess.mutateAsync({ granteeEmail: trimmed, role: 'editor' });
    if (result.inviteToken) {
      const inviteUrl = buildCvAccessInviteUrl(result.inviteToken, result.inviteUrl);
      setLastInviteUrl(inviteUrl);
      setLastAccessMessage(t('cv.sharePanel.people.inviteLinkCreated'));
    } else {
      setLastInviteUrl(null);
      setLastAccessMessage(
        t('cv.sharePanel.people.grantedDirect', {
          email: result.grant.granteeEmail ?? trimmed,
        }),
      );
    }
    setEmail('');
  };

  const handleRevoke = async (grantId: string) => {
    setRevokingGrantId(grantId);
    try {
      await revokeAccess.mutateAsync(grantId);
      setLastInviteUrl(null);
      setLastAccessMessage(t('cv.sharePanel.people.revoked'));
    } finally {
      setRevokingGrantId(null);
    }
  };

  const handleDownload = async (format: ExportFormat) => {
    if (isFormatLoading(format) || !selectedEdition.ready) return;
    if (!canDownloadFormat(format)) {
      onLockedFormat();
      return;
    }
    setDownloadFormat(format);
    setDownloadError(null);
    try {
      await onDownload(format, { ...(selectedEdition.selection ?? { variantId: selectedVariantId }), visibility });
    } catch (error) {
      setDownloadError(
        error instanceof Error
          ? error.message
          : t('cv.share.downloadFailed'),
      );
    } finally {
      setDownloadFormat(null);
    }
  };

  return (
    <div className="tabbio-cv-share">
      <style>{sharePanelCss}</style>
      {cvReviewEnabled ? <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <CvEditionSelector target={target} value={selectedVariantId} onChange={(value) => { setSelectedVariantId(value); setReleaseChoice({ mode: 'follow_approved' }); }} />
      {tab === 'download' && selectedVariantId ? <CvSelectedEditionPreview document={selectedEdition.data} pending={selectedEdition.isPending} unavailable={!selectedEdition.isPending && !selectedEdition.ready} /> : null}
        <CvShareReleaseChoice target={target} variantId={selectedVariantId} value={releaseChoice} onChange={setReleaseChoice} />
        <NativeButton title={t('cv.review.title')} variant="outline" onPress={() => router.push({ pathname: '/cv/review', params: { target } })} />
      </div> : null}
      <div className="tabbio-cv-share-grid">
        <aside className="tabbio-cv-share-side" aria-label={t('cv.share.title')}>
          <section className="tabbio-cv-share-summary">
            <span className="tabbio-cv-share-disc" aria-hidden>
              <MaterialSymbol name="description" size={22} />
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={summaryTitle}>{title}</span>
              {publicUrl ? <span style={summarySub}>{publicUrl}</span> : null}
            </span>
          </section>

          <nav className="tabbio-cv-share-nav" role="tablist" aria-orientation="vertical">
            {tabs.map((item) => (
              <TabButton
                key={item.id}
                active={tab === item.id}
                icon={item.icon}
                label={t(item.labelKey)}
                onClick={() => setTab(item.id)}
              />
            ))}
          </nav>

        </aside>

        <div className="tabbio-cv-share-content">
          {tab === 'link' ? (
            <section className="tabbio-cv-share-section">
              <SectionHeader
                icon="ios_share"
                title={t('cv.sharePanel.link.title')}
              />
              <VisibilityGrid visibility={visibility} onChange={setVisibility} />
              <ToggleRow
                selected={allowDownload}
                label={t('cv.sharePanel.link.allowDownload')}
                onClick={() => setAllowDownload((value) => !value)}
              />
              <div className="tabbio-cv-share-actions">
                <button
                  type="button"
                  style={primaryButton(createShare.isPending)}
                  disabled={createShare.isPending}
                  onClick={handleCreateShare}
                >
                  {createShare.isPending
                    ? t('cv.sharePanel.link.creating')
                    : activeShare
                      ? t('cv.sharePanel.link.createNew')
                      : t('cv.sharePanel.link.create')}
                </button>
                {activeShare ? (
                  <button
                    type="button"
                    className="tabbio-chip"
                    onClick={() =>
                      updateShare.mutate({
                        shareLinkId: activeShare.id,
                        visibility: visibilityPayload(visibility),
                        allowDownload,
                      })
                    }
                  >
                    <MaterialSymbol name="tune" size={16} />
                    {t('cv.sharePanel.link.saveVisibility')}
                  </button>
                ) : null}
              </div>
              {lastShareUrl ? (
                <CopyBar
                  value={lastShareUrl}
                  copied={copiedValue === lastShareUrl}
                  onCopy={() => handleCopy(lastShareUrl, 'copy')}
                />
              ) : null}
            </section>
          ) : null}

          {tab === 'people' ? (
            <section className="tabbio-cv-share-section">
              <SectionHeader
                icon="group"
                title={t('cv.sharePanel.people.title')}
              />
              <div className="tabbio-cv-share-invite">
                <div style={{ position: 'relative', minWidth: 0 }}>
                  <MaterialSymbol name="mail" size={18} style={inputIcon} />
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder={t('cv.sharePanel.people.emailPlaceholder')}
                    style={input}
                  />
                </div>
                <button
                  type="button"
                  style={primaryButton(createAccess.isPending)}
                  disabled={createAccess.isPending}
                  onClick={handleInvite}
                >
                  {createAccess.isPending
                    ? t('cv.sharePanel.people.inviting')
                    : t('cv.sharePanel.people.invite')}
                </button>
              </div>
              {lastInviteUrl ? (
                <CopyBar
                  value={lastInviteUrl}
                  copied={copiedValue === lastInviteUrl}
                  onCopy={() => handleCopy(lastInviteUrl)}
                />
              ) : null}
              {lastAccessMessage ? <div style={noticeText}>{lastAccessMessage}</div> : null}
              <div className="tabbio-cv-share-list">
                {(accessGrants.data ?? []).map((grant) => (
                  <div key={grant.id} className="tabbio-cv-share-row">
                    <span style={{ minWidth: 0 }}>
                      <span style={rowTitle}>
                        {grant.granteeEmail ?? grant.granteeUserId ?? t('cv.sharePanel.people.invitedUser')}
                      </span>
                      <span style={rowSub}>
                        {t(`cv.sharePanel.people.status.${grant.status}`, grant.status)}
                      </span>
                    </span>
                    <span className="tabbio-cv-share-row-actions">
                      <span className="tabbio-menu-badge">{grant.role}</span>
                      <button
                        type="button"
                        className="tabbio-chip"
                        disabled={revokingGrantId === grant.id}
                        onClick={() => handleRevoke(grant.id)}
                      >
                        <MaterialSymbol name="lock" size={15} />
                        {revokingGrantId === grant.id
                          ? t('cv.sharePanel.people.revoking')
                          : t('cv.sharePanel.people.revoke')}
                      </button>
                    </span>
                  </div>
                ))}
                {!accessGrants.data?.length ? (
                  <div style={emptyText}>{t('cv.sharePanel.people.empty')}</div>
                ) : null}
              </div>
            </section>
          ) : null}

          {tab === 'download' ? (
            <section className="tabbio-cv-share-section">
              <SectionHeader
                icon="download"
                title={t('cv.sharePanel.download.title')}
              />
              <VisibilityGrid visibility={visibility} onChange={setVisibility} />
              <div className="tabbio-cv-share-list">
                {FORMATS.map((format) => {
                  const locked = !canDownloadFormat(format);
                  const disabled = downloadFormat !== null || isFormatLoading(format);
                  return (
                    <button
                      key={format}
                      type="button"
                      className="tabbio-cv-share-download-row"
                      disabled={disabled}
                      style={{ opacity: disabled ? 0.45 : 1 }}
                      onClick={() => handleDownload(format)}
                    >
                      <span style={downloadLabel}>{format.toUpperCase()}</span>
                      <span style={downloadMeta}>
                        {downloadFormat === format
                          ? t('cv.sharePanel.download.downloading')
                          : locked
                            ? <MaterialSymbol name="lock" size={16} />
                            : (
                              <>
                                {t('cv.sharePanel.download.download')}
                                <MaterialSymbol name="download" size={16} />
                              </>
                            )}
                      </span>
                    </button>
                  );
                })}
                {downloadError ? <div style={errorText}>{downloadError}</div> : null}
              </div>
            </section>
          ) : null}

          {tab === 'activity' ? (
            <section className="tabbio-cv-share-section">
              <SectionHeader
                icon="history"
                title={t('cv.sharePanel.activity.title', { title })}
              />
              <div className="tabbio-cv-share-list">
                {(activity.data ?? []).map((item) => (
                  <div key={item.id} className="tabbio-cv-share-row">
                    <span style={rowTitle}>{formatAction(item.action)}</span>
                    <span style={rowSub}>{new Date(item.createdAt).toLocaleDateString()}</span>
                  </div>
                ))}
                {!activity.data?.length ? (
                  <div style={emptyText}>{t('cv.sharePanel.activity.empty')}</div>
                ) : null}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function VisibilityGrid({
  visibility,
  onChange,
}: {
  visibility: CvVisibilityOptions;
  onChange: (visibility: CvVisibilityOptions) => void;
}) {
  const { t } = useTranslation('seeker');
  const items: Array<[keyof CvVisibilityOptions, string]> = [
    ['showEmail', 'cv.sharePanel.visibility.email'],
    ['showPhone', 'cv.sharePanel.visibility.phone'],
    ['showLocation', 'cv.sharePanel.visibility.location'],
    ['showLinks', 'cv.sharePanel.visibility.links'],
    ['showPhoto', 'cv.sharePanel.visibility.photo'],
  ];
  return (
    <div className="tabbio-cv-share-visibility">
      {items.map(([key, label]) => (
        <ToggleRow
          key={key}
          selected={visibility[key]}
          label={t(label)}
          onClick={() => onChange({ ...visibility, [key]: !visibility[key] })}
        />
      ))}
    </div>
  );
}

function ToggleRow({
  selected,
  label,
  onClick,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={selected ? 'tabbio-cv-share-toggle is-selected' : 'tabbio-cv-share-toggle'}
      onClick={onClick}
    >
      <MaterialSymbol
        name={selected ? 'check_circle' : 'radio_button_unchecked'}
        size={19}
        style={{ color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)' }}
      />
      <span>{label}</span>
    </button>
  );
}

function TabButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? 'tabbio-cv-share-tab is-active' : 'tabbio-cv-share-tab'}
      onClick={onClick}
    >
      <MaterialSymbol name={icon} size={19} />
      <span>{label}</span>
      {active ? <MaterialSymbol name="check" size={16} style={{ marginInlineStart: 'auto' }} /> : null}
    </button>
  );
}

function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <header style={sectionHeader}>
      <span className="tabbio-cv-share-disc" aria-hidden>
        <MaterialSymbol name={icon} size={20} />
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={sectionTitle}>{title}</span>
        {subtitle ? <span style={sectionSub}>{subtitle}</span> : null}
      </span>
    </header>
  );
}

function CopyBar({
  value,
  copied,
  onCopy,
}: {
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation('seeker');
  return (
    <div className="tabbio-cv-share-copybar">
      <span dir="auto" style={copyValue}>
        {value}
      </span>
      <button type="button" className="tabbio-chip" onClick={onCopy}>
        <MaterialSymbol name={copied ? 'check' : 'content_copy'} size={16} />
        {copied ? t('cv.sharePanel.copied') : t('cv.sharePanel.copy')}
      </button>
    </div>
  );
}

function formatAction(action: string) {
  return action.replace(/^cv\./, '').replaceAll('.', ' ');
}

const primaryButton = (disabled?: boolean): CSSProperties => ({
  minHeight: 44,
  borderRadius: 9999,
  border: 0,
  paddingInline: 18,
  background: 'var(--text-primary)',
  color: 'var(--bg)',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  whiteSpace: 'nowrap',
});

const summaryTitle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 15,
  fontWeight: 500,
  color: 'var(--text-primary)',
};

const summarySub: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginTop: 3,
  fontSize: 12,
  color: 'var(--text-tertiary)',
};

const sectionHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  minWidth: 0,
};

const sectionTitle: CSSProperties = {
  display: 'block',
  fontSize: 18,
  lineHeight: '24px',
  fontWeight: 500,
  color: 'var(--text-primary)',
};

const sectionSub: CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: 13,
  lineHeight: '19px',
  color: 'var(--text-secondary)',
};

const inputIcon: CSSProperties = {
  position: 'absolute',
  insetBlockStart: '50%',
  insetInlineStart: 18,
  transform: 'translateY(-50%)',
  color: 'var(--text-secondary)',
  pointerEvents: 'none',
};

const input: CSSProperties = {
  width: '100%',
  height: 52,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  paddingInlineStart: 48,
  paddingInlineEnd: 20,
  fontSize: 15,
  color: 'var(--text-primary)',
  backgroundColor: 'var(--input-bg)',
  boxShadow: 'var(--gem-input-shadow)',
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const noticeText: CSSProperties = {
  borderRadius: 18,
  padding: '12px 14px',
  background: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  boxShadow: 'var(--gem-input-shadow)',
  color: 'var(--text-secondary)',
  fontSize: 13,
  lineHeight: '19px',
};

const rowTitle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-primary)',
};

const rowSub: CSSProperties = {
  display: 'block',
  marginTop: 3,
  fontSize: 12,
  color: 'var(--text-tertiary)',
};

const emptyText: CSSProperties = {
  color: 'var(--text-tertiary)',
  fontSize: 13,
};

const errorText: CSSProperties = {
  color: 'var(--color-danger, #d92d20)',
  fontSize: 13,
  lineHeight: '18px',
  fontWeight: 500,
};

const downloadLabel: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-primary)',
};

const downloadMeta: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: 'var(--text-secondary)',
  fontSize: 13,
};

const copyValue: CSSProperties = {
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--text-secondary)',
  fontSize: 13,
};

const sharePanelCss = `
.tabbio-cv-share {
  font-family: 'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif;
  color: var(--text-primary);
}

.tabbio-cv-share-grid {
  display: grid;
  grid-template-columns: minmax(188px, 220px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.tabbio-cv-share-side,
.tabbio-cv-share-content,
.tabbio-cv-share-list,
.tabbio-cv-share-chipwrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.tabbio-cv-share-summary,
.tabbio-cv-share-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(0,0,0,0.04);
}

[data-theme='dark'] .tabbio-cv-share-summary,
[data-theme='dark'] .tabbio-cv-share-section,
[data-theme='dark'] .tabbio-cv-share-toggle.is-selected,
[data-theme='dark'] .tabbio-cv-share-tab.is-active,
[data-theme='dark'] .tabbio-cv-share-download-row:hover {
  background: rgba(255,255,255,0.06);
}

.tabbio-cv-share-summary {
  flex-direction: row;
  align-items: center;
  gap: 12px;
}

.tabbio-cv-share-disc {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(0,0,0,0.06);
  color: var(--text-secondary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

[data-theme='dark'] .tabbio-cv-share-disc {
  background: rgba(255,255,255,0.08);
}

.tabbio-cv-share-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tabbio-cv-share-tab {
  min-height: 42px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 0;
  border-radius: 9999px;
  padding: 0 14px;
  background: transparent;
  color: var(--text-secondary);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: start;
}

.tabbio-cv-share-tab:hover,
.tabbio-cv-share-tab.is-active,
.tabbio-cv-share-toggle.is-selected,
.tabbio-cv-share-download-row:hover {
  background: rgba(0,0,0,0.06);
  color: var(--text-primary);
}

.tabbio-cv-share-language {
  gap: 14px;
}

.tabbio-cv-share-visibility {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.tabbio-cv-share-toggle,
.tabbio-cv-share-download-row {
  min-height: 48px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--input-border);
  border-radius: 18px;
  background: var(--input-bg);
  color: var(--text-secondary);
  box-shadow: var(--gem-input-shadow);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: start;
  padding: 0 14px;
}

.tabbio-cv-share-download-row {
  justify-content: space-between;
}

.tabbio-cv-share-actions,
.tabbio-cv-share-invite,
.tabbio-cv-share-copybar,
.tabbio-cv-share-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.tabbio-cv-share-invite {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
}

.tabbio-cv-share-copybar {
  min-height: 50px;
  padding: 8px 8px 8px 16px;
  border-radius: 20px;
  background: var(--input-bg);
  border: 1px solid var(--input-border);
  box-shadow: var(--gem-input-shadow);
}

.tabbio-cv-share-row {
  justify-content: space-between;
  padding: 12px 0;
  border-top: 1px solid var(--input-border);
}

.tabbio-cv-share-row:first-child {
  border-top: 0;
}

.tabbio-cv-share-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.tabbio-cv-share button:disabled {
  opacity: 0.45;
  cursor: default;
}

@media (max-width: 760px) {
  .tabbio-cv-share-grid {
    grid-template-columns: 1fr;
  }

  .tabbio-cv-share-nav {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tabbio-cv-share-tab {
    justify-content: center;
  }

  .tabbio-cv-share-tab .material-symbols-outlined:last-child {
    display: none;
  }

  .tabbio-cv-share-visibility,
  .tabbio-cv-share-invite {
    grid-template-columns: 1fr;
  }

  .tabbio-cv-share-actions,
  .tabbio-cv-share-copybar,
  .tabbio-cv-share-row,
  .tabbio-cv-share-row-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .tabbio-cv-share-actions > *,
  .tabbio-cv-share-row-actions > *,
  .tabbio-cv-share-invite > button {
    width: 100%;
    justify-content: center;
  }
}
`;
